import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Eye, Trash2, StickyNote } from "lucide-react";
import { api, API_ROOT, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const FOLLOW_UP_AFTER_MS = 3 * 24 * 3600 * 1000;
const needsFollowUp = (e) =>
  ["new", "contacted", "quoted"].includes(e.status || "new") &&
  Date.now() - new Date(e.status_updated_at || e.created_at).getTime() > FOLLOW_UP_AFTER_MS;

const TYPE_LABELS = { domestic: "Wholesale", export: "Export", quote: "Quote", general: "General" };
const STATUS_STAGES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_STYLES = {
  new: "bg-gold/20 text-gold-dark",
  contacted: "bg-indigo/10 text-indigo",
  quoted: "bg-[#1FA855]/15 text-[#178a45]",
  won: "bg-indigo text-white",
  lost: "bg-[#B42318]/10 text-[#B42318]",
};
const inputCls =
  "w-full rounded-sm border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-indigo focus:ring-1 focus:ring-indigo";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [lostPrompt, setLostPrompt] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [followUpOnly, setFollowUpOnly] = useState(false);

  const visibleEnquiries = followUpOnly ? enquiries.filter(needsFollowUp) : enquiries;

  const openEnquiry = (e) => {
    setSelected(e);
    setNoteText("");
    setLostPrompt(false);
    setLostReason("");
  };

  const changeStatus = async (id, status, reason = "") => {
    try {
      await api.patch(`/admin/enquiries/${id}/status`, { status, reason });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status, status_reason: status === "lost" ? reason : "" } : e)));
      setSelected((s) => (s && s.id === id ? { ...s, status, status_reason: status === "lost" ? reason : "" } : s));
      setLostPrompt(false);
      setLostReason("");
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleStageClick = (stage) => {
    if (stage === (selected?.status || "new")) return; // already at this stage — keep existing reason
    if (stage === "lost") {
      setLostPrompt(true);
      return;
    }
    changeStatus(selected.id, stage);
  };

  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/admin/enquiries/${selected.id}/notes`, { text: noteText.trim() });
      const notes = [...(selected.notes || []), data];
      setSelected((s) => ({ ...s, notes }));
      setEnquiries((list) => list.map((e) => (e.id === selected.id ? { ...e, notes } : e)));
      setNoteText("");
      toast.success("Note saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/admin/enquiries/${selected.id}/notes/${noteId}`);
      const notes = (selected.notes || []).filter((n) => n.id !== noteId);
      setSelected((s) => ({ ...s, notes }));
      setEnquiries((list) => list.map((e) => (e.id === selected.id ? { ...e, notes } : e)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated. Use the new password next time you sign in.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("type", filter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const qs = params.toString();
      const [enq, st] = await Promise.all([
        api.get(`/admin/enquiries${qs ? `?${qs}` : ""}`),
        api.get("/admin/stats"),
      ]);
      setEnquiries(enq.data.items);
      setStats(st.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [filter, statusFilter]);

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    if (user && tab === "settings" && !settings) {
      api.get("/admin/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(formatApiError(e)));
    }
  }, [user, tab, settings]);

  if (!user) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground" data-testid="admin-loading">Loading…</div>;
  }

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const setS = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Seo title="Admin — Shineni India" description="Shineni India enquiry admin." />
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg font-bold tracking-wide text-indigo">SHINENI</span>
            <span className="font-display text-[10px] font-semibold tracking-[0.35em] text-gold">ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-foreground/60 transition-colors hover:text-indigo" data-testid="admin-view-site">View site</a>
            <button
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 rounded-sm border border-border px-3 py-2 text-xs font-medium text-foreground/70 transition-colors hover:border-indigo hover:text-indigo"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex gap-2" role="tablist">
          {["enquiries", "settings"].map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-sm border px-4 py-2 text-sm font-medium capitalize transition-colors ${
                tab === t ? "border-indigo bg-indigo text-white" : "border-border bg-white text-foreground/70 hover:text-indigo"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-8" data-testid="admin-enquiries-panel">
            {stats && (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                {[["all", "Total"], ["domestic", "Wholesale"], ["export", "Export"], ["quote", "Quotes"], ["general", "General"]].map(([k, label]) => (
                  <div key={k} className="border border-border bg-white p-4" data-testid={`admin-stat-${k}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
                    <p className="mt-1 font-display text-2xl font-semibold text-indigo">
                      {k === "all" ? stats.total : stats.by_type[k] || 0}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                {["all", "domestic", "export", "quote", "general"].map((t) => (
                  <button
                    key={t}
                    data-testid={`admin-filter-${t}`}
                    onClick={() => setFilter(t)}
                    className={`rounded-sm border px-3 py-1.5 text-xs font-medium transition-colors ${
                      filter === t ? "border-indigo bg-indigo text-white" : "border-border bg-white text-foreground/70 hover:text-indigo"
                    }`}
                  >
                    {t === "all" ? "All" : TYPE_LABELS[t]}
                  </button>
                ))}
                <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
                {["all", ...STATUS_STAGES].map((s) => (
                  <button
                    key={s}
                    data-testid={`admin-stage-filter-${s}`}
                    onClick={() => setStatusFilter(s)}
                    className={`rounded-sm border px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                      statusFilter === s ? "border-gold bg-gold text-indigo-deep" : "border-border bg-white text-foreground/70 hover:text-indigo"
                    }`}
                  >
                    {s === "all" ? "Any stage" : s}
                  </button>
                ))}
                <button
                  data-testid="admin-filter-followup"
                  onClick={() => setFollowUpOnly((v) => !v)}
                  className={`rounded-sm border px-3 py-1.5 text-xs font-medium transition-colors ${
                    followUpOnly ? "border-[#B45309] bg-[#B45309] text-white" : "border-[#B45309]/40 bg-white text-[#B45309] hover:bg-[#B45309]/10"
                  }`}
                >
                  Needs follow-up
                </button>
              </div>
              <div className="flex gap-2">
                <button onClick={load} data-testid="admin-refresh-button"
                  className="flex items-center gap-1.5 rounded-sm border border-border bg-white px-3 py-2 text-xs font-medium text-foreground/70 transition-colors hover:text-indigo">
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
                  <a
                  href={`${API_ROOT}/api/admin/enquiries/export${(() => { const p = new URLSearchParams(); if (filter !== "all") p.set("type", filter); if (statusFilter !== "all") p.set("status", statusFilter); const q = p.toString(); return q ? `?${q}` : ""; })()}`}
                  data-testid="admin-export-csv"
                  className="flex items-center gap-1.5 rounded-sm bg-gold px-3 py-2 text-xs font-semibold text-indigo-deep transition-colors hover:bg-gold-dark"
                >
                  <Download className="h-3.5 w-3.5" /> Export CSV
                </a>
              </div>
            </div>

            <div className="mt-4 overflow-x-auto border border-border bg-white">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-border bg-background text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Company</th>
                    <th className="px-4 py-3 font-semibold">Country</th>
                    <th className="px-4 py-3 font-semibold">Product</th>
                    <th className="px-4 py-3 font-semibold">Qty</th>
                    <th className="px-4 py-3 font-semibold">Stage</th>
                    <th className="px-4 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEnquiries.map((e) => (
                    <tr key={e.id} className="border-b border-border/60 last:border-0 hover:bg-background/60" data-testid={`enquiry-row-${e.id}`}>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-sm bg-indigo/10 px-2 py-0.5 text-[11px] font-semibold text-indigo">{TYPE_LABELS[e.enquiry_type]}</span>
                      </td>
                      <td className="px-4 py-3 font-medium">{e.name}</td>
                      <td className="px-4 py-3">{e.company || "—"}</td>
                      <td className="px-4 py-3">{e.country}</td>
                      <td className="px-4 py-3">{e.product}</td>
                      <td className="px-4 py-3">{e.quantity}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`}
                          data-testid={`enquiry-status-${e.id}`}>
                          {e.status || "new"}
                        </span>
                        {needsFollowUp(e) && (
                          <span className="ml-1.5 rounded-sm bg-[#B45309]/10 px-2 py-0.5 text-[11px] font-semibold text-[#B45309]"
                            data-testid={`enquiry-followup-${e.id}`}>
                            Follow up
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => openEnquiry(e)} data-testid={`enquiry-view-${e.id}`}
                          className="flex items-center gap-1 text-xs font-medium text-indigo hover:text-gold-dark">
                          <Eye className="h-3.5 w-3.5" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {visibleEnquiries.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="enquiries-empty">No enquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "settings" && settings && (
          <form onSubmit={saveSettings} className="mt-8 max-w-2xl border border-border bg-white p-6 sm:p-10" data-testid="admin-settings-form">
            <h2 className="font-display text-xl font-semibold text-indigo">Business settings</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              These details appear across the site. Publish registrations (GST / IEC) only after verification.
            </p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">WhatsApp number</label>
                <input className={inputCls} data-testid="settings-whatsapp" value={settings.whatsapp_number} onChange={setS("whatsapp_number")} placeholder="+91 98XXXXXXXX" />
                <p className="mt-1 text-[11px] text-muted-foreground">Enables the WhatsApp buttons site-wide when set.</p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">Business email</label>
                <input className={inputCls} data-testid="settings-business-email" value={settings.business_email} onChange={setS("business_email")} placeholder="sales@yourcompany.com" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">Phone</label>
                <input className={inputCls} data-testid="settings-phone" value={settings.phone} onChange={setS("phone")} placeholder="+91 …" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">Address</label>
                <input className={inputCls} data-testid="settings-address" value={settings.address} onChange={setS("address")} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">GST number</label>
                <input className={inputCls} data-testid="settings-gst" value={settings.gst} onChange={setS("gst")} placeholder="Blank until verified" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">IEC code</label>
                <input className={inputCls} data-testid="settings-iec" value={settings.iec} onChange={setS("iec")} placeholder="Blank until verified" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">Enquiry notification email</label>
                <input className={inputCls} data-testid="settings-notification-email" type="email" value={settings.notification_email} onChange={setS("notification_email")} placeholder="Where new enquiries are emailed" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm" data-testid="settings-email-toggle-label">
                  <input type="checkbox" data-testid="settings-email-toggle" checked={settings.email_notifications_enabled} onChange={setS("email_notifications_enabled")} className="h-4 w-4 accent-[#1A2B4C]" />
                  Email me on every new enquiry
                </label>
              </div>
            </div>
            <button type="submit" disabled={savingSettings} data-testid="settings-save-button"
              className="mt-8 rounded-sm bg-indigo px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-deep disabled:opacity-60">
              {savingSettings ? "Saving…" : "Save settings"}
            </button>
          </form>
        )}

        {tab === "settings" && (
          <form onSubmit={changePassword} className="mt-8 max-w-2xl border border-border bg-white p-6 sm:p-10" data-testid="admin-password-form">
            <h2 className="font-display text-xl font-semibold text-indigo">Change password</h2>
            <p className="mt-1 text-xs text-muted-foreground">Minimum 8 characters. Takes effect on your next sign-in.</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">Current password</label>
                <input type="password" required autoComplete="current-password" className={inputCls} data-testid="pw-current"
                  value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">New password</label>
                <input type="password" required minLength={8} autoComplete="new-password" className={inputCls} data-testid="pw-new"
                  value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">Confirm new</label>
                <input type="password" required minLength={8} autoComplete="new-password" className={inputCls} data-testid="pw-confirm"
                  value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} />
              </div>
            </div>
            <button type="submit" disabled={savingPw} data-testid="pw-save-button"
              className="mt-6 rounded-sm border border-indigo px-8 py-3 text-sm font-semibold text-indigo transition-colors hover:bg-indigo hover:text-white disabled:opacity-60">
              {savingPw ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>

      <Dialog open={Boolean(selected)} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto" data-testid="enquiry-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-indigo">
              {selected?.name} {selected?.company ? `· ${selected.company}` : ""}
            </DialogTitle>
            <DialogDescription>{selected ? TYPE_LABELS[selected.enquiry_type] : ""} enquiry</DialogDescription>
          </DialogHeader>
          {selected && (
            <dl className="grid grid-cols-3 gap-y-3 text-sm">
              {[["Email", selected.email], ["WhatsApp", selected.whatsapp], ["Country", selected.country],
                ["Product", selected.product], ["Quantity", selected.quantity], ["Destination", selected.destination || "—"],
                ["Submitted", new Date(selected.created_at).toLocaleString("en-IN")]].map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="col-span-2 font-medium">{v}</dd>
                </div>
              ))}
              {selected.attachment && (
                <div className="contents">
                  <dt className="text-muted-foreground">Tech pack</dt>
                  <dd className="col-span-2">
                    <a
                      href={`${API_ROOT}/api/admin/files/${selected.attachment.file_id}`}
                      data-testid="enquiry-attachment-download"
                      className="inline-flex items-center gap-1.5 font-medium text-indigo transition-colors hover:text-gold-dark"
                    >
                      <Download className="h-3.5 w-3.5" /> {selected.attachment.filename}
                      <span className="text-xs text-muted-foreground">({(selected.attachment.size / 1024).toFixed(0)} KB)</span>
                    </a>
                  </dd>
                </div>
              )}
              <dt className="text-muted-foreground">Specifications</dt>
              <dd className="col-span-2 whitespace-pre-wrap">{selected.specifications || "—"}</dd>
              <dt className="text-muted-foreground">Message</dt>
              <dd className="col-span-2 whitespace-pre-wrap">{selected.message || "—"}</dd>
              <dt className="text-muted-foreground">Stage</dt>
              <dd className="col-span-2">
                <div className="flex flex-wrap gap-2" data-testid="enquiry-stage-picker">
                  {STATUS_STAGES.map((s) => (
                    <button
                      key={s}
                      data-testid={`stage-${s}`}
                      onClick={() => handleStageClick(s)}
                      className={`rounded-sm border px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${
                        (selected.status || "new") === s
                          ? "border-indigo bg-indigo text-white"
                          : "border-border bg-white text-foreground/70 hover:border-indigo hover:text-indigo"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {lostPrompt && (
                  <div className="mt-3 rounded-sm border border-[#B42318]/30 bg-[#B42318]/5 p-3" data-testid="lost-reason-prompt">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#B42318]">Why was this deal lost?</label>
                    <div className="flex gap-2">
                      <input
                        className={inputCls}
                        data-testid="lost-reason-input"
                        placeholder="e.g. Price too high, went to another supplier"
                        value={lostReason}
                        onChange={(e) => setLostReason(e.target.value)}
                      />
                      <button
                        onClick={() => changeStatus(selected.id, "lost", lostReason.trim())}
                        disabled={!lostReason.trim()}
                        data-testid="lost-confirm-button"
                        className="shrink-0 rounded-sm bg-[#B42318] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#8f1a12] disabled:opacity-50"
                      >
                        Mark lost
                      </button>
                    </div>
                  </div>
                )}
              </dd>
              {selected.status === "lost" && selected.status_reason && (
                <>
                  <dt className="text-muted-foreground">Lost reason</dt>
                  <dd className="col-span-2 font-medium text-[#B42318]" data-testid="lost-reason-text">{selected.status_reason}</dd>
                </>
              )}
            </dl>
          )}

          {selected && (
            <div className="mt-6 border-t border-border pt-5" data-testid="enquiry-notes-section">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-indigo">
                <StickyNote className="h-4 w-4 text-gold" /> Conversation notes
              </h3>
              <div className="mt-4 space-y-3">
                {(selected.notes || []).length === 0 && (
                  <p className="text-xs text-muted-foreground" data-testid="notes-empty">No notes yet — record calls, quotes and follow-ups here.</p>
                )}
                {(selected.notes || []).map((n) => (
                  <div key={n.id} className="group flex items-start justify-between gap-3 rounded-sm border border-border bg-background p-3" data-testid={`note-${n.id}`}>
                    <div>
                      <p className="whitespace-pre-wrap text-sm">{n.text}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {new Date(n.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>
                    <button onClick={() => deleteNote(n.id)} data-testid={`note-delete-${n.id}`} aria-label="Delete note"
                      className="text-muted-foreground opacity-0 transition-opacity hover:text-[#B42318] group-hover:opacity-100">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <textarea
                  rows={2}
                  className={inputCls}
                  data-testid="note-input"
                  placeholder="e.g. Called buyer — quoted ₹410/pc for 2,000 pcs, follow-up on Friday"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <button
                  onClick={addNote}
                  disabled={savingNote || !noteText.trim()}
                  data-testid="note-add-button"
                  className="shrink-0 self-end rounded-sm bg-indigo px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-deep disabled:opacity-50"
                >
                  {savingNote ? "Saving…" : "Add note"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
